import React, { useState, useEffect } from 'react';
import { getProjects } from '../../services/projectService';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectOption } from '../ui/select';
import { PageLoading } from '../ui/Loading';
import { formatCurrency } from '../../lib/utils';
import { 
  BarChart3, 
  TrendingUp,
  DollarSign,
  Users,
  Calendar,
  PieChart,
  Target,
  Award,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';

const Analytics = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('all');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const { data, error } = await getProjects();
      if (error) throw error;
      setProjects(data || []);
    } catch (err) {
      console.error('Error loading projects:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter projects by time range
  const getFilteredProjects = () => {
    if (timeRange === 'all') return projects;
    
    const now = new Date();
    const filterDate = new Date();
    
    switch (timeRange) {
      case 'thisMonth':
        filterDate.setMonth(now.getMonth());
        filterDate.setDate(1);
        break;
      case 'lastMonth':
        filterDate.setMonth(now.getMonth() - 1);
        filterDate.setDate(1);
        break;
      case 'thisQuarter':
        const quarter = Math.floor(now.getMonth() / 3);
        filterDate.setMonth(quarter * 3);
        filterDate.setDate(1);
        break;
      case 'thisYear':
        filterDate.setMonth(0);
        filterDate.setDate(1);
        break;
      default:
        return projects;
    }
    
    return projects.filter(project => 
      new Date(project.created_at) >= filterDate
    );
  };

  const filteredProjects = getFilteredProjects();

  // Calculate key metrics
  const calculateMetrics = () => {
    const totalRevenue = filteredProjects.reduce((sum, project) => 
      sum + (parseFloat(project.project_price) || 0), 0
    );
    
    const totalPaid = filteredProjects.reduce((sum, project) => 
      sum + (parseFloat(project.totalPaidAmount) || 0), 0
    );
    
    const totalOutstanding = totalRevenue - totalPaid;
    
    const completedProjects = filteredProjects.filter(project => {
      const paidAmount = project.totalPaidAmount || 0;
      const totalAmount = project.totalPaymentAmount || 0;
      return totalAmount > 0 && paidAmount >= totalAmount;
    }).length;
    
    const inProgressProjects = filteredProjects.filter(project => {
      const paidAmount = project.totalPaidAmount || 0;
      const totalAmount = project.totalPaymentAmount || 0;
      return totalAmount > 0 && paidAmount > 0 && paidAmount < totalAmount;
    }).length;
    
    const pendingProjects = filteredProjects.filter(project => {
      const paidAmount = project.totalPaidAmount || 0;
      return paidAmount === 0;
    }).length;
    
    const averageProjectValue = filteredProjects.length > 0 
      ? totalRevenue / filteredProjects.length 
      : 0;
    
    const completionRate = filteredProjects.length > 0 
      ? (completedProjects / filteredProjects.length) * 100 
      : 0;
    
    const paymentRate = totalRevenue > 0 
      ? (totalPaid / totalRevenue) * 100 
      : 0;

    return {
      totalRevenue,
      totalPaid,
      totalOutstanding,
      completedProjects,
      inProgressProjects,
      pendingProjects,
      averageProjectValue,
      completionRate,
      paymentRate,
      totalProjects: filteredProjects.length
    };
  };

  const metrics = calculateMetrics();

  // Get project status distribution
  const getStatusDistribution = () => {
    const statusCounts = {
      completed: 0,
      inProgress: 0,
      pending: 0
    };

    filteredProjects.forEach(project => {
      const paidAmount = project.totalPaidAmount || 0;
      const totalAmount = project.totalPaymentAmount || 0;
      
      if (totalAmount > 0 && paidAmount >= totalAmount) {
        statusCounts.completed++;
      } else if (totalAmount > 0 && paidAmount > 0) {
        statusCounts.inProgress++;
      } else {
        statusCounts.pending++;
      }
    });

    return statusCounts;
  };

  const statusDistribution = getStatusDistribution();

  // Get monthly revenue trend
  const getMonthlyTrend = () => {
    const monthlyData = {};
    
    filteredProjects.forEach(project => {
      const date = new Date(project.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          revenue: 0,
          paid: 0,
          projects: 0
        };
      }
      
      monthlyData[monthKey].revenue += parseFloat(project.project_price) || 0;
      monthlyData[monthKey].paid += parseFloat(project.totalPaidAmount) || 0;
      monthlyData[monthKey].projects += 1;
    });

    return Object.entries(monthlyData)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-12); // Last 12 months
  };

  const monthlyTrend = getMonthlyTrend();

  // Get top clients
  const getTopClients = () => {
    const clientData = {};
    
    filteredProjects.forEach(project => {
      const client = project.client_name;
      if (!clientData[client]) {
        clientData[client] = {
          revenue: 0,
          projects: 0,
          paid: 0
        };
      }
      
      clientData[client].revenue += parseFloat(project.project_price) || 0;
      clientData[client].paid += parseFloat(project.totalPaidAmount) || 0;
      clientData[client].projects += 1;
    });

    return Object.entries(clientData)
      .map(([client, data]) => ({ client, ...data }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);
  };

  const topClients = getTopClients();

  if (loading) {
    return <PageLoading message="กำลังโหลดข้อมูล Analytics..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
            <BarChart3 className="h-6 w-6" />
            <span>Analytics Dashboard</span>
          </h1>
          <p className="text-gray-600 mt-1">ภาพรวมและสถิติสำหรับผู้บริหาร</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-600">ช่วงเวลา:</span>
            <Select value={timeRange} onValueChange={setTimeRange} className="w-32">
              <SelectOption value="all">ทั้งหมด</SelectOption>
              <SelectOption value="thisMonth">เดือนนี้</SelectOption>
              <SelectOption value="lastMonth">เดือนที่แล้ว</SelectOption>
              <SelectOption value="thisQuarter">ไตรมาสนี้</SelectOption>
              <SelectOption value="thisYear">ปีนี้</SelectOption>
            </Select>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">รายได้รวม</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(metrics.totalRevenue)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {metrics.totalProjects} โปรเจค
                </p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">ชำระแล้ว</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(metrics.totalPaid)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {metrics.paymentRate.toFixed(1)}% ของยอดรวม
                </p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">ค้างชำระ</p>
                <p className="text-2xl font-bold text-orange-600">
                  {formatCurrency(metrics.totalOutstanding)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  รอการชำระ
                </p>
              </div>
              <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">อัตราเสร็จสิ้น</p>
                <p className="text-2xl font-bold text-purple-600">
                  {metrics.completionRate.toFixed(1)}%
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {metrics.completedProjects}/{metrics.totalProjects} โปรเจค
                </p>
              </div>
              <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Target className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Project Status Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <PieChart className="h-5 w-5" />
              <span>สถานะโปรเจค</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-800">เสร็จสิ้น</span>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-green-600">{statusDistribution.completed}</p>
                  <p className="text-xs text-green-600">
                    {metrics.totalProjects > 0 ? ((statusDistribution.completed / metrics.totalProjects) * 100).toFixed(1) : 0}%
                  </p>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Clock className="h-5 w-5 text-yellow-600" />
                  <span className="font-medium text-yellow-800">กำลังดำเนินการ</span>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-yellow-600">{statusDistribution.inProgress}</p>
                  <p className="text-xs text-yellow-600">
                    {metrics.totalProjects > 0 ? ((statusDistribution.inProgress / metrics.totalProjects) * 100).toFixed(1) : 0}%
                  </p>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <AlertCircle className="h-5 w-5 text-gray-600" />
                  <span className="font-medium text-gray-800">รอดำเนินการ</span>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-600">{statusDistribution.pending}</p>
                  <p className="text-xs text-gray-600">
                    {metrics.totalProjects > 0 ? ((statusDistribution.pending / metrics.totalProjects) * 100).toFixed(1) : 0}%
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5" />
              <span>ลูกค้าสำคัญ</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {topClients.slice(0, 5).map((client, index) => (
                <div key={client.client} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-bold text-blue-600">#{index + 1}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{client.client}</p>
                      <p className="text-xs text-gray-500">{client.projects} โปรเจค</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">{formatCurrency(client.revenue)}</p>
                    <p className="text-xs text-gray-500">
                      {formatCurrency(client.paid)} ชำระแล้ว
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5" />
            <span>แนวโน้มรายได้รายเดือน</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {monthlyTrend.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {monthlyTrend.slice(-6).map(([month, data]) => {
                  const monthName = new Date(month + '-01').toLocaleDateString('th-TH', { 
                    month: 'short', 
                    year: 'numeric' 
                  });
                  
                  return (
                    <div key={month} className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                      <div className="text-center">
                        <p className="text-sm font-medium text-blue-800 mb-2">{monthName}</p>
                        <div className="space-y-2">
                          <div>
                            <p className="text-xs text-blue-600">รายได้รวม</p>
                            <p className="text-lg font-bold text-blue-800">
                              {formatCurrency(data.revenue)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-green-600">ชำระแล้ว</p>
                            <p className="text-sm font-semibold text-green-700">
                              {formatCurrency(data.paid)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600">โปรเจค</p>
                            <p className="text-sm font-semibold text-gray-700">
                              {data.projects} โปรเจค
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">ไม่มีข้อมูลในช่วงเวลาที่เลือก</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Performance Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="h-12 w-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Award className="h-6 w-6 text-indigo-600" />
              </div>
              <p className="text-sm font-medium text-gray-600 mb-1">มูลค่าโปรเจคเฉลี่ย</p>
              <p className="text-2xl font-bold text-indigo-600">
                {formatCurrency(metrics.averageProjectValue)}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="h-12 w-12 bg-emerald-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Users className="h-6 w-6 text-emerald-600" />
              </div>
              <p className="text-sm font-medium text-gray-600 mb-1">ลูกค้าทั้งหมด</p>
              <p className="text-2xl font-bold text-emerald-600">
                {new Set(filteredProjects.map(p => p.client_name)).size}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="h-12 w-12 bg-rose-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Calendar className="h-6 w-6 text-rose-600" />
              </div>
              <p className="text-sm font-medium text-gray-600 mb-1">โปรเจคในเดือนนี้</p>
              <p className="text-2xl font-bold text-rose-600">
                {filteredProjects.filter(p => {
                  const projectDate = new Date(p.created_at);
                  const now = new Date();
                  return projectDate.getMonth() === now.getMonth() && 
                         projectDate.getFullYear() === now.getFullYear();
                }).length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Analytics;
